import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_CONFIG from '../../apiConfig';
import ImageUpload from '../image-editing/ImageUpload';
import InsufficientCreditsModal from '../ui/InsufficientCreditsModal';
import { useCreditEstimation } from '../../hooks/useCreditEstimation';
import { useModeRestrictions } from '../../hooks/useModeRestrictions';
import { ModelTemplate, BackgroundPreset } from './types';
import { frontBackExamplePresets } from './examplePresets';
import { useWorkflowData } from './hooks/useWorkflowData';

// Modular Components
import { WorkflowStepper } from './components/WorkflowStepper';
import { ModelGrid } from './components/ModelGrid';
import { BackgroundGrid } from './components/BackgroundGrid';
import { GeneratedResults } from './components/GeneratedResults';
import { SummarySidebar } from './components/SummarySidebar';

type Step = 'SELECT_PRODUCTS' | 'SELECT_MODELS' | 'SELECT_BACKGROUND' | 'SUMMARY';

interface FrontBackWorkflowProps {
  onBackToWorkflows: () => void;
  initialInputImages?: string[];
  initialGeneratedImages?: string[];
  initialModelImageUrl?: string;
  initialBackgroundImageUrl?: string;
}

const FrontBackWorkflow: React.FC<FrontBackWorkflowProps> = ({ onBackToWorkflows, initialInputImages, initialGeneratedImages, initialModelImageUrl, initialBackgroundImageUrl }) => {
  const navigate = useNavigate();
  const { currentUser, refreshUserCredits, userCredits } = useAuth();

  const [currentStep, setCurrentStep] = useState<Step>(initialGeneratedImages?.length ? 'SUMMARY' : 'SELECT_PRODUCTS');
  const [frontImageUrl, setFrontImageUrl] = useState(initialInputImages?.[0] ?? '');
  const [backImageUrl, setBackImageUrl] = useState(initialInputImages?.[1] ?? '');
  const [generatedImages, setGeneratedImages] = useState<string[]>(initialGeneratedImages || []);
  const [prompt, setPrompt] = useState('');
  const [mode, setMode] = useState('performance');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedTemplateUrl, setSelectedTemplateUrl] = useState<string | null>(initialModelImageUrl ?? null);
  const [selectedBackgroundId, setSelectedBackgroundId] = useState<string | null>(null);
  const [selectedBackgroundUrl, setSelectedBackgroundUrl] = useState<string | null>(initialBackgroundImageUrl ?? null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [isExample, setIsExample] = useState(false);

  const [examplePresetIdForSummary, setExamplePresetIdForSummary] = useState<number | null>(null);
  const { models, modelsLoading, allBackgrounds, backgroundsLoading } = useWorkflowData();
  const [backgroundPresets, setBackgroundPresets] = useState<BackgroundPreset[]>([]);
  const [genderFilter, setGenderFilter] = useState<'female' | 'male'>('female');
  const [backgroundCategory, setBackgroundCategory] = useState('all');
  const [backgroundCategories, setBackgroundCategories] = useState<{ value: string; label: string }[]>([]);

  const [creditEstimation, estimateCreditCost] = useCreditEstimation('ai-workflow');
  const modeRestrictions = useModeRestrictions();

  const skipAutoNavRef = useRef(false);
  const prevFrontImageRef = useRef('');
  const prevBackImageRef = useRef('');
  const prevModelRef = useRef<string | null>(null);
  const prevBackgroundRef = useRef<string | null>(null);

  const inputImageUrls = [frontImageUrl, backImageUrl].filter(Boolean);

  useEffect(() => {
    if (!currentUser?.uid) return;
    estimateCreditCost({
      workflowType: 'front-back-shoot',
      inputImageUrls: inputImageUrls,
      userId: currentUser.uid,
      mode: mode.toLowerCase(),
      numImages: 1,
      ...(selectedTemplateId && { templateId: selectedTemplateId }),
      ...(selectedBackgroundId && { backgroundId: selectedBackgroundId }),
      ...(prompt?.trim() && { userPrompt: prompt.trim() }),
    });
  }, [currentUser?.uid, frontImageUrl, backImageUrl, selectedTemplateId, selectedBackgroundId, prompt, mode, estimateCreditCost]);

  useEffect(() => {
    if (allBackgrounds.length > 0) {
      const uniqueCats = Array.from(new Set(allBackgrounds.map((bg: BackgroundPreset) => bg.category).filter(Boolean))) as string[];
      uniqueCats.sort((a, b) => a.localeCompare(b));
      const solidIndex = uniqueCats.findIndex(cat => cat.toLowerCase() === 'solid');
      if (solidIndex > -1) {
        const [solid] = uniqueCats.splice(solidIndex, 1);
        uniqueCats.unshift(solid);
      }

      const dynamicCategories = [
        { value: 'all', label: 'All' },
        ...uniqueCats.map((cat: string) => ({
          value: cat.toLowerCase(),
          label: cat.charAt(0).toUpperCase() + cat.slice(1)
        }))
      ];
      setBackgroundCategories(dynamicCategories);
      setBackgroundPresets(allBackgrounds);
    }
  }, [allBackgrounds]);

  useEffect(() => {
    if (backgroundCategory === 'all') {
      setBackgroundPresets(allBackgrounds);
    } else {
      setBackgroundPresets(allBackgrounds.filter((bg: BackgroundPreset) => bg.category.toLowerCase() === backgroundCategory));
    }
  }, [backgroundCategory, allBackgrounds]);

  useEffect(() => {
    if (examplePresetIdForSummary == null || models.length === 0 || allBackgrounds.length === 0) return;
    const preset = frontBackExamplePresets.find((p) => p.id === examplePresetIdForSummary) as typeof frontBackExamplePresets[0] & { presetModelName?: string; presetBackgroundId?: string };
    if (!preset?.presetModelName || !preset?.presetBackgroundId) {
      setExamplePresetIdForSummary(null);
      return;
    }
    const model = models.find((m: ModelTemplate) => m.name.toLowerCase() === preset.presetModelName!.toLowerCase());
    const bg = allBackgrounds.find((b: BackgroundPreset) => b.backgroundId === preset.presetBackgroundId);
    if (model) {
      setSelectedTemplateId(model.id);
      setSelectedTemplateUrl(model.imageUrl);
      prevModelRef.current = model.id;
    }
    if (bg) {
      setSelectedBackgroundId(bg.backgroundId);
      setSelectedBackgroundUrl(bg.imageUrl);
      prevBackgroundRef.current = bg.backgroundId;
    }
    skipAutoNavRef.current = true;
    setCurrentStep('SUMMARY');
    setExamplePresetIdForSummary(null);
  }, [examplePresetIdForSummary, models, allBackgrounds]);

  useEffect(() => {
    if (skipAutoNavRef.current) return;
    if (currentStep === 'SELECT_PRODUCTS' && frontImageUrl && backImageUrl) {
      const isNew = frontImageUrl !== prevFrontImageRef.current || backImageUrl !== prevBackImageRef.current;
      if (isNew) {
        prevFrontImageRef.current = frontImageUrl;
        prevBackImageRef.current = backImageUrl;
        const t = setTimeout(() => setCurrentStep('SELECT_MODELS'), 500);
        return () => clearTimeout(t);
      }
    }
  }, [currentStep, frontImageUrl, backImageUrl]);

  useEffect(() => {
    if (skipAutoNavRef.current) return;
    if (currentStep === 'SELECT_MODELS' && selectedTemplateId && selectedTemplateId !== prevModelRef.current) {
      prevModelRef.current = selectedTemplateId;
      const t = setTimeout(() => setCurrentStep('SELECT_BACKGROUND'), 300);
      return () => clearTimeout(t);
    }
  }, [currentStep, selectedTemplateId]);

  useEffect(() => {
    if (skipAutoNavRef.current) return;
    if (currentStep === 'SELECT_BACKGROUND' && selectedBackgroundId && selectedBackgroundId !== prevBackgroundRef.current) {
      prevBackgroundRef.current = selectedBackgroundId;
      const t = setTimeout(() => setCurrentStep('SUMMARY'), 300);
      return () => clearTimeout(t);
    }
  }, [currentStep, selectedBackgroundId]);

  useEffect(() => {
    if (skipAutoNavRef.current) {
      const t = setTimeout(() => { skipAutoNavRef.current = false; }, 100);
      return () => clearTimeout(t);
    }
  }, [currentStep]);

  const handleGenerate = async () => {
    if (!creditEstimation.canAfford) {
      setShowInsufficientCreditsModal(true);
      return;
    }
    setIsProcessing(true);
    setIsExample(false);
    try {
      const payload: Record<string, unknown> = {
        inputImageUrls: [frontImageUrl, backImageUrl].filter(Boolean),
        userId: currentUser?.uid || 'anonymous',
        workflowType: 'front-back-shoot',
        mode: mode.toLowerCase(),
        numImages: 1,
        imageSize: 'auto',
        outputFormat: 'webp',
        enableSafetyChecker: true,
      };
      if (selectedBackgroundUrl) payload.backgroundImageUrl = selectedBackgroundUrl;
      if (selectedTemplateUrl) payload.modelImageUrl = selectedTemplateUrl;
      if (prompt?.trim()) payload.userPrompt = prompt.trim();

      const res = await fetch(API_CONFIG.AI_WORKFLOW_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.success && Array.isArray(data.imageUrls) && data.imageUrls.length > 0) {
        setGeneratedImages(data.imageUrls);
        await refreshUserCredits();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsProcessing(false);
    }
  };

  const steps: { key: Step; label: string }[] = [
    { key: 'SELECT_PRODUCTS', label: 'Upload Images' },
    { key: 'SELECT_MODELS', label: 'Select Model' },
    { key: 'SELECT_BACKGROUND', label: 'Select Background' },
    { key: 'SUMMARY', label: 'Summary' },
  ];

  const nextStep = () => {
    const idx = steps.findIndex(s => s.key === currentStep);
    if (currentStep === 'SELECT_PRODUCTS' && (!frontImageUrl || !backImageUrl)) {
      alert('Please upload both front and back images.');
      return;
    }
    if (idx < steps.length - 1) setCurrentStep(steps[idx + 1].key);
  };

  const prevStep = () => {
    const idx = steps.findIndex(s => s.key === currentStep);
    if (idx <= 0) {
      onBackToWorkflows();
      return;
    }
    skipAutoNavRef.current = true;
    setCurrentStep(steps[idx - 1].key);
  };

  const goToStep = (step: Step) => {
    const stepIdx = steps.findIndex(s => s.key === step);
    const currentIdx = steps.findIndex(s => s.key === currentStep);
    if (stepIdx >= 0 && stepIdx < currentIdx) {
      skipAutoNavRef.current = true;
      setCurrentStep(step);
    }
  };

  const handleStartNew = () => {
    setGeneratedImages([]);
    setFrontImageUrl('');
    setBackImageUrl('');
    setSelectedTemplateId(null);
    setSelectedTemplateUrl(null);
    setSelectedBackgroundId(null);
    setSelectedBackgroundUrl(null);
    setPrompt('');
    setIsExample(false);
    setCurrentStep('SELECT_PRODUCTS');
    onBackToWorkflows();
  };

  const handleExampleSelect = (presetId: number) => {
    const preset = frontBackExamplePresets.find((p) => p.id === presetId);
    if (!preset) return;
    setFrontImageUrl(preset.frontPreview);
    setBackImageUrl(preset.backPreview);
    setPrompt(preset.prompt);
    setGeneratedImages(preset.results ?? []);
    setIsExample(true);
    const p = preset as typeof preset & { presetModelName?: string; presetBackgroundId?: string };
    if (p.presetModelName && p.presetBackgroundId) {
      skipAutoNavRef.current = true;
      setExamplePresetIdForSummary(presetId);
    }
  };

  const handleDownloadResult = async (url: string, index: number) => {
    try {
      const r = await fetch(url);
      const blob = await r.blob();
      const u = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u;
      a.download = `ai-workflow-${index + 1}.jpg`;
      a.click();
      window.URL.revokeObjectURL(u);
    } catch (err) {
      const a = document.createElement('a');
      a.href = url;
      a.download = `ai-workflow-${index + 1}.jpg`;
      a.click();
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <WorkflowStepper
        steps={steps}
        currentStep={currentStep}
        onStepClick={goToStep}
        onPrev={prevStep}
        onNext={currentStep !== 'SUMMARY' ? nextStep : undefined}
      />

      <div className="max-w-[1600px] mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-5">
          <div className={currentStep !== 'SUMMARY' ? 'lg:col-span-4' : 'lg:col-span-3'}>
            {currentStep === 'SELECT_PRODUCTS' && (
              <div className="flex flex-col items-center justify-center py-2 sm:py-4">
                <div className="w-full max-w-5xl mx-auto">
                  <div className="text-center mb-4">
                    <span className="inline-block bg-gray-100 px-3 py-1 rounded-full text-[10px] font-semibold text-gray-600 mb-2 uppercase tracking-wider">Front & Back Shoot</span>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900">Upload Images to Begin</h3>
                    <p className="text-xs sm:text-sm text-gray-500">PNG, JPG, WebP, AVIF or HEIC</p>
                  </div>

                  <div className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto">
                      <ImageUpload
                        imageUrls={frontImageUrl ? [frontImageUrl] : []}
                        setImageUrls={(urls: any) => {
                          const u = typeof urls === 'function' ? urls([]) : urls;
                          setFrontImageUrl(u.length ? u[0] : '');
                          setIsExample(false);
                        }}
                        title="Front Image"
                        uploadUrl={API_CONFIG.AI_WORKFLOW_UPLOAD_URL}
                        isExample={isExample}
                      />
                      <ImageUpload
                        imageUrls={backImageUrl ? [backImageUrl] : []}
                        setImageUrls={(urls: any) => {
                          const u = typeof urls === 'function' ? urls([]) : urls;
                          setBackImageUrl(u.length ? u[0] : '');
                          setIsExample(false);
                        }}
                        title="Back Image"
                        uploadUrl={API_CONFIG.AI_WORKFLOW_UPLOAD_URL}
                        isExample={isExample}
                      />
                    </div>

                    <div className="max-w-4xl mx-auto mt-6 bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
                      <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center">
                        <i className="fas fa-lightbulb text-secondary-500 mr-2"></i>
                        Or start with an example
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {frontBackExamplePresets.map((preset) => (
                          <div
                            key={preset.id}
                            className={`group relative cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${isExample && frontImageUrl === preset.frontPreview ? 'border-secondary-500 ring-2 ring-secondary-100' : 'border-transparent hover:border-gray-200 shadow-sm'
                              }`}
                            onClick={() => handleExampleSelect(preset.id)}
                          >
                            <div className="grid grid-cols-2 gap-[1px] bg-gray-100 aspect-[3/2]">
                              <div className="h-full w-full overflow-hidden">
                                <img src={preset.frontPreview} alt="Front" className="w-full h-full object-cover group-hover:scale-110 transition-all duration-500" />
                              </div>
                              <div className="h-full w-full overflow-hidden">
                                <img src={preset.backPreview} alt="Back" className="w-full h-full object-cover group-hover:scale-110 transition-all duration-500" />
                              </div>
                            </div>
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                              <p className="text-white text-[10px] font-bold uppercase tracking-tight">{preset.name}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 'SELECT_MODELS' && (
              <ModelGrid
                models={models}
                loading={modelsLoading}
                selectedId={selectedTemplateId}
                onSelect={(model) => {
                  if (selectedTemplateId === model.id) {
                    setSelectedTemplateId(null);
                    setSelectedTemplateUrl(null);
                  } else {
                    setSelectedTemplateId(model.id);
                    setSelectedTemplateUrl(model.imageUrl);
                  }
                }}
                genderFilter={genderFilter}
                onGenderChange={setGenderFilter}
              />
            )}

            {currentStep === 'SELECT_BACKGROUND' && (
              <BackgroundGrid
                backgrounds={backgroundPresets}
                loading={backgroundsLoading}
                selectedId={selectedBackgroundId}
                onSelect={(id, url) => {
                  setSelectedBackgroundId(id);
                  setSelectedBackgroundUrl(url);
                }}
                categories={backgroundCategories}
                currentCategory={backgroundCategory}
                onCategoryChange={setBackgroundCategory}
              />
            )}

            {currentStep === 'SUMMARY' && (
              <div>
                {generatedImages.length > 0 ? (
                  <GeneratedResults
                    images={generatedImages}
                    onStartNew={handleStartNew}
                    onDownload={handleDownloadResult}
                    onNavigateToCanvas={(url, tool) => navigate('/canvas-editor', { state: { inputImages: [url], tool } })}
                  />
                ) : (
                  <div className="hidden lg:flex flex-col items-center justify-center bg-white rounded-2xl border border-gray-200 shadow-sm" style={{ minHeight: 'calc(100vh - 220px)' }}>
                    <div className="text-center p-8">
                      <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6"><i className="fas fa-images text-gray-400 text-3xl"></i></div>
                      <h3 className="text-xl font-semibold text-gray-700 mb-2">Ready to Generate</h3>
                      <p className="text-sm text-gray-500 max-w-md">Review your selections in the sidebar and click Generate.</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {currentStep === 'SUMMARY' && (
            <div className="lg:col-span-1">
              <SummarySidebar
                inputImageUrls={inputImageUrls}
                selectedModelUrl={selectedTemplateUrl}
                selectedModelName={selectedTemplateId ? (models.find((m: ModelTemplate) => m.id === selectedTemplateId)?.name ?? selectedTemplateId) : selectedTemplateUrl ? 'Selected' : 'Not selected'}
                selectedBackgroundUrl={selectedBackgroundUrl}
                selectedBackgroundName={selectedBackgroundId === 'none' ? 'No Change' : selectedBackgroundId && selectedBackgroundId !== 'from-gallery' ? selectedBackgroundId : selectedBackgroundUrl ? 'Selected' : 'Not selected'}
                mode={mode}
                onModeChange={setMode}
                availableModes={modeRestrictions.availableModes}
                prompt={prompt}
                onPromptChange={setPrompt}
                showAdvancedSettings={showAdvancedSettings}
                onToggleAdvanced={() => setShowAdvancedSettings(!showAdvancedSettings)}
                creditEstimation={creditEstimation}
                onGenerate={handleGenerate}
                isProcessing={isProcessing}
              />
            </div>
          )}
        </div>
      </div>

      <InsufficientCreditsModal isOpen={showInsufficientCreditsModal} onClose={() => setShowInsufficientCreditsModal(false)} requiredCredits={creditEstimation.creditCost || 0} availableCredits={userCredits} featureName="AI Workflow" />
    </div>
  );
};

export default FrontBackWorkflow;
